import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getErrorState,
  getLoadingStatus,
  getPaymentState,
} from 'scenes/payments/redux/payment/selectors';
import useNextPage from 'scenes/payments/hooks';
import {
  CreditCardPaymentStep,
  DirectDebitPaymentStep,
} from 'scenes/common/components/Steps';
import {
  ApiState,
  CreditCardDetails,
  DirectDebitDetails,
  PaymentMethod,
} from 'scenes/common/types';
import { PreviousButton } from 'scenes/common/components/Header/PreviousButton';
import { PaymentResult } from 'scenes/payments/api/types';
import { payByCreditCard, payByDirectDebit } from 'scenes/payments/api/payment';
import { LoadingSpinner } from 'components';
import usePreviousPage from 'scenes/payments/hooks/usePreviousPage';
import { onBackGoToLanding } from 'scenes/payments/routes';
import { History } from 'history';
import { useHistory } from 'react-router';
import { ErrorModal, ErrorType } from 'scenes/payments/components/Errors';
import { updateError } from 'scenes/payments/redux/payment/thunks';

export const MakePayment: FunctionComponent = () => {
  const nextPage = useNextPage();
  const previousPage = usePreviousPage();
  const paymentState = useSelector(getPaymentState);
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const errorState = useSelector(getErrorState);
  const loadingStatus = useSelector(getLoadingStatus);

  const { payment } = paymentState;

  const payMethod = payment?.paymentMethod
    ? payment?.paymentMethod
    : PaymentMethod.CreditCard;

  useEffect(() => {
    if (
      errorState?.name === ApiState.BusinessError &&
      loadingStatus === ApiState.BusinessError
    ) {
      setErrorModalOpen(true);
    }
  }, [errorState, loadingStatus]);

  const handleCreditCardPayment = async (
    creditCardDetails: CreditCardDetails
  ) => {
    try {
      setIsLoading(true);
      const payResult = await payByCreditCard(
        paymentState,
        creditCardDetails,
        dispatch
      );
      setIsLoading(false);
      if (payResult === PaymentResult.Success) {
        nextPage();
      }
    } catch (error) {
      console.warn('Error happened for payByCreditCard: ', error.message);
    }
  };

  const handleDirectDebitPayment = async (
    directDebitDetails: DirectDebitDetails
  ) => {
    try {
      setIsLoading(true);
      const payResult = await payByDirectDebit(
        paymentState,
        directDebitDetails,
        dispatch
      );
      setIsLoading(false);
      if (payResult === PaymentResult.Success) {
        nextPage();
      }
    } catch (error) {
      console.warn('Error happened for payByDirectDebit', error.message);
    }
  };

  const history: History = useHistory();
  useEffect(() => {
    if (errorModalOpen) {
      onBackGoToLanding(history);
    }
  });
  const handlePrevious = () => previousPage();

  return (
    <>
      {isLoading && <LoadingSpinner label={' '} />}
      {!isLoading && (
        <div>
          <PreviousButton onPrevious={handlePrevious} hasPreviousButton />
          {errorModalOpen && (
            <ErrorModal
              error={ErrorType.PaymentUnsuccessful}
              onClose={() => {
                setErrorModalOpen(!errorModalOpen);
                dispatch(updateError(null));
              }}
            />
          )}
          {payMethod === PaymentMethod.CreditCard && (
            <CreditCardPaymentStep
              onCreditCardPayment={handleCreditCardPayment}
              buttonText="Pay now"
              paymentPortal
            />
          )}
          {payMethod === PaymentMethod.DirectDebit && (
            <DirectDebitPaymentStep
              onDirectDebitPayment={handleDirectDebitPayment}
              buttonText="Pay now"
            />
          )}
        </div>
      )}
    </>
  );
};

export default MakePayment;
